import React, {useState, useEffect} from 'react';
import {useLocation, useParams, useNavigate} from 'react-router-dom';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Button,
  Stack,
} from '@mui/material';
import PropTypes from 'prop-types';
import {
  useUpdateDocumentMutation,
  useCreateSupportingDocumentsDownloadUrlMutation,
  useGetDocumentQuery,
} from '../data/documentWorkspaceApi';
import {useSelector, useDispatch} from 'react-redux';
import {setDocument} from '../data/documents';
import {triggerDownload} from '../util';
import _ from 'lodash';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import step0 from '../assets/rollforward-walk-through/step-0.svg';
import step1 from '../assets/rollforward-walk-through/step-1.svg';
import step2 from '../assets/rollforward-walk-through/step-2.svg';
import step3 from '../assets/rollforward-walk-through/step-3.svg';
import step4 from '../assets/rollforward-walk-through/step-4.svg';
import step5 from '../assets/rollforward-walk-through/step-5.svg';
import step6 from '../assets/rollforward-walk-through/step-6.svg';

const steps = Array.from({length: 7}, (_) => '');

// Custom StepIcon component that always shows numbers (never check marks)
const CustomStepIcon = (props) => {
  const {active, completed, icon} = props;

  let stepStyle = {};

  if (completed) {
    // Completed step - green background with white text (same green as Download button)
    stepStyle = {
      backgroundColor: '#26A993',
      color: '#FFFFFF',
      border: '1px solid #26A993',
    };
  } else if (active) {
    // Active step - grey outline with transparent background
    stepStyle = {
      backgroundColor: 'transparent',
      color: 'black',
      border: '1px solid black',
    };
  } else {
    // Inactive step - translucent background with grey text and circle
    stepStyle = {
      backgroundColor: 'transparent',
      color: '#819190',
      border: '1px solid #819190',
    };
  }

  return (
    <Box
      sx={{
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.75rem',
        fontWeight: '600',
        ...stepStyle,
      }}
    >
      {icon}
    </Box>
  );
};

CustomStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
  icon: PropTypes.number.isRequired,
};

const stepSubtitles = [
  'Download the following file to your computer to sync the numbers to your report. ',
  `Open the file and click "Yes" on this alert.`,
  `Open the file and click "Update" on this alert.`,
  `Open the file and click "Delete" on this alert.`,
  `Click "Manage Workbook Links"`,
  `Open this menu and click "Change Source" and select the source.`,
  'Select the source file',
];

const styles = {
  container: {
    display: 'flex',
    width: '460px',
    minHeight: 'fit-content',
    flexDirection: 'column',
    alignItems: 'flex-start',
    borderRadius: '24px',
    border: '1px solid #E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  title: {
    color: '#162523',
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '150%',
    letterSpacing: '-0.2px',
    alignSelf: 'stretch',
  },
  content: {
    display: 'flex',
    padding: '32px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '24px',
    alignSelf: 'stretch',
  },
  subTitle: {
    color: '#1C3937',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '130%',
    letterSpacing: '-0.14px',
  },
  divider: {
    borderColor: '#DDe0DE',
    borderWidth: '0.0625rem',
    opacity: 1,
    height: '0.0625rem',
    width: '100%',
  },
  button: {
    background: '#26A993',
    borderRadius: '0.5rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '2.25rem',
    minWidth: '3.5rem',
    color: '#FFFFFF',
    fontSize: '0.875rem',
    fontWeight: '400',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#229884',
    },
    '&:disabled': {
      backgroundColor: '#81C3B8',
      color: '#FFFFFF',
    },
  },
  backButton: {
    background: 'transparent',
    borderRadius: '0.5rem',
    paddingLeft: '0.75rem',
    paddingRight: '1rem',
    height: '2.25rem',
    minWidth: '3.5rem',
    color: '#043B34',
    fontSize: '0.875rem',
    fontWeight: '400',
    textTransform: 'none',
    border: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      border: 'none',
      boxShadow: 'none',
    },
    '&:focus': {
      backgroundColor: 'transparent',
      border: 'none',
      boxShadow: 'none',
    },
  },
  checkbox: {
    color: '#819190',
    '&.Mui-checked': {
      color: '#26A993',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
    },
  },
};

const RollForwardWalkThroughPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {documentId} = useParams();
  const location = useLocation();
  const {currentOrg, isAuthenticated} = useSelector((state) => state.session);
  const documentFromState = location.state?.document;

  const {data: documentData, error: documentError} = useGetDocumentQuery(
    {documentId},
    {
      skip:
        _.isNil(documentId) ||
        _.isNil(currentOrg) ||
        !isAuthenticated ||
        !!documentFromState,
      refetchOnMountOrArgChange: true,
    }
  );

  const document = documentFromState || documentData;

  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (documentError) {
      console.error('Error getting document:', documentError);
    }
  }, [documentError]);

  useEffect(() => {
    if (document) {
      dispatch(setDocument({document}));
    }
  }, [document, dispatch]);

  const [updateDocument] = useUpdateDocumentMutation();
  const [
    createSupportingDocumentsDownloadUrl,
    {data, isSuccess, isError, error},
  ] = useCreateSupportingDocumentsDownloadUrlMutation();
  const handleDownloadClick = async () => {
    const result = await createSupportingDocumentsDownloadUrl({
      documentId: document.documentId,
    });
    if (result.error) {
      console.error(
        'Error creating supporting documents download url:',
        result.error
      );
      return;
    }

    setActiveStep(activeStep + 1);
  };
  const handleNextClick = () => {
    if (activeStep === steps.length - 1) {
      return;
    }
    setActiveStep(activeStep + 1);
  };
  const handlePreviousClick = () => {
    if (activeStep === 0) {
      return;
    }
    setActiveStep(activeStep - 1);
  };

  const handleOpenReportClick = async () => {
    await updateDocument({
      documentId: document.documentId,
      data: {
        ingestionState: {
          showWalkThrough: false,
        },
      },
    });
    navigate(`/documents/${document.documentId}`);
  };

  const getStepImage = (step) => {
    switch (step) {
      case 0:
        return step0;
      case 1:
        return step1;
      case 2:
        return step2;
      case 3:
        return step3;
      case 4:
        return step4;
      case 5:
        return step5;
      case 6:
        return step6;
      default:
        return step0;
    }
  };

  const justifyContent = () => {
    if (activeStep === 0) {
      return 'flex-end';
    }
    return 'space-between';
  };

  useEffect(() => {
    if (isSuccess) {
      triggerDownload(
        () => ({signedUrl: data.supportingDocumentsSignedUrl}),
        `${document.name} - [Supporting Documents].zip`
      );
    } else if (isError) {
      console.error('Error downloading supporting document', {error});
    }
  }, [data, isSuccess, isError, error]);

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#F2F4F4',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: '11rem',
        gap: '1rem',
      }}
    >
      <Box sx={styles.container}>
        <Box sx={styles.content}>
          <Typography sx={styles.title}>
            Complete the following steps to finish the roll forward
          </Typography>
          {activeStep !== steps.length - 1 && (
            <Stepper
              activeStep={activeStep}
              orientation="horizontal"
              sx={{
                gap: '0.33rem',
                '& .MuiStepLabel-label': {
                  display: 'none !important',
                },
              }}
            >
              {steps.map((step, index) => (
                <Step key={index} completed={index < activeStep}>
                  <StepLabel
                    StepIconComponent={CustomStepIcon}
                    icon={index + 1}
                  />
                </Step>
              ))}
            </Stepper>
          )}
          <Typography sx={styles.subTitle}>
            {stepSubtitles[activeStep]}
          </Typography>
          <img
            src={getStepImage(activeStep)}
            alt="Roll Forward Walk Through"
            style={{alignSelf: 'center'}}
          />
        </Box>

        <Divider orientation={'horizontal'} sx={styles.divider} />

        <Stack
          direction={'row'}
          paddingY={'1rem'}
          paddingX={'1.5rem'}
          justifyContent={justifyContent()}
          alignItems={'center'}
          width={'100%'}
          sx={{width: '100%'}}
        >
          {activeStep === 0 ? (
            <Button
              variant="contained"
              sx={styles.button}
              onClick={handleDownloadClick}
            >
              Download
            </Button>
          ) : (
            <>
              {activeStep !== 0 && (
                <Button
                  variant="outlined"
                  sx={styles.backButton}
                  onClick={handlePreviousClick}
                  startIcon={<ChevronLeftIcon />}
                >
                  Back
                </Button>
              )}
              {activeStep !== steps.length - 1 && (
                <Button
                  variant="contained"
                  sx={styles.button}
                  onClick={handleNextClick}
                >
                  Next
                </Button>
              )}
              {activeStep === steps.length - 1 && (
                <Button
                  variant="contained"
                  sx={styles.button}
                  onClick={handleOpenReportClick}
                >
                  Open Report
                </Button>
              )}
            </>
          )}
        </Stack>
      </Box>
    </div>
  );
};

export default RollForwardWalkThroughPage;
